// i18n area 'backups' (task #127): sections ckpt, backup.
// Split out of the old monolithic dictionaries.ts by scripts/split-dictionaries.mjs;
// since the split, these files are the SOURCE — add and edit keys here. The script
// only re-runs to re-split a merged monolithic dictionaries.ts (see its header).

export const enUS = {
  ckpt: {
    contents: 'Accepts',
    contentsHint:
      'What may land here. "Checkpoints" is the cluster configuration, "Data backups" are the VM backups: the two readings compose, so one destination may accept both.',
    content: { checkpoints: 'Checkpoints', backups: 'Data backups' },
    destinationsTitle: 'Backup destinations',
    checkpointsTitle: 'Checkpoints',
    restoreTitle: 'Restore',
    addDestination: 'Add destination',
    addDestinationTitle: 'Add backup destination',
    editDestinationTitle: 'Edit destination',
    storage: 'Storage',
    storageHint:
      'A dir-like storage (dir/NFS/CIFS/CephFS) — the artifacts are plain files under the platform namespace.',
    schedule: 'Schedule',
    scheduleNone: 'Manual only',
    scheduleHourly: 'Hourly',
    scheduleDaily: 'Daily',
    scheduleWeekly: 'Weekly',
    scheduleHour: 'Hour',
    scheduleWeekday: 'Weekday',
    scheduleHint:
      'Scheduled checkpoints have no operator to type a masterpass: their keystore secrets are packed as placeholders.',
    retention: 'Retention',
    retentionHint:
      'Scheduled checkpoints to keep — the sweeper removes the oldest beyond the count. Manual and pre-restore checkpoints are never swept.',
    paused: 'Paused',
    pausedHint:
      'The operator blackout: while paused, nothing is scheduled and nothing is pruned on this destination — data policies and checkpoints alike. Running jobs are never touched.',
    orphanRuns: '{n} orphaned runs',
    orphanRunsHint:
      'Scheduled runs of policies that no longer exist on this destination: no retention governs them — never counted, never pruned — and only a hand deletes them. The "backups" lens of tvirt diagnose names the removed policies.',
    rescan: 'Rescan artifacts',
    rescanTitle: 'Rescan artifacts of {name}',
    rescanHint:
      'Rebuilds the backup catalog (VM/CT/App) from the manifests of the artifacts on this destination. Existing rows are never touched; backups of unknown policies stay orphans (never pruned automatically). The move after a bootstrap restore.',
    masterpassDrWarning:
      'Disaster scenario: LUKS disks come back ONLY from a checkpoint WITH a masterpass. Scheduled checkpoints pack placeholders — take a manual checkpoint with a masterpass periodically.',
    policy: {
      title: 'Data backup policies',
      hint: 'Named policies: which objects (kind, tags, scopes), how often, when a run may start, and how many copies to keep. The enrolment gesture stays with the object owner: tags are assigned on its own scope.',
      add: 'Add policy',
      column: 'Data policies',
      name: 'Name',
      nameHint:
        "The policy label, unique within the destination. Runs follow the policy's stable id: renaming is free; deleting it orphans its runs (no longer counted or pruned).",
      kindLocked: 'The object family of an existing policy cannot change: create a new policy.',
      kindLabel: 'Objects',
      kind: { vm: 'Virtual machines', ct: 'Containers', app: 'Apps' },
      tags: 'Tag selector',
      tagsHint: 'tag=value pairs, comma separated, ANDed. Empty tags and scopes select nothing — never everything.',
      scopes: 'Scopes',
      scopesHint: 'Scope paths (subtrees), comma separated, ORed with each other.',
      windowStart: 'Window from',
      windowEnd: 'Window to',
      windowHint:
        'Execution window (HH:MM, wall clock; start past end wraps midnight). It gates the START only: a run that overruns it is never killed.',
      mode: 'Mode',
      modeHint: 'Incremental rides the series family; a broken series degrades to a full, spoken in the journal.',
      modeFull: 'Full',
      modeIncremental: 'Incremental',
      requireQuiesce: 'Require quiesce (guest agent)',
      paused: 'Policy paused',
      retentionNone: 'Keep everything',
      retentionCount: 'Count',
      retentionGfs: 'GFS rings',
      gfs: { keepLast: 'Keep last', daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly' },
      gfsHint:
        'For each ring the newest backup of each of the last N periods is kept. A kept backup keeps its whole chain alive — never a base with orphans.',
      lastRun: 'Last run',
      neverRan: 'never run'
    },
    lastCheckpoint: 'Last checkpoint',
    neverRun: 'Never taken',
    createNow: 'Create checkpoint',
    createNowTitle: 'Create checkpoint — {name}',
    masterpass: 'Masterpass',
    masterpassHint:
      'Protects the keystore secret values (argon2id → AES-256-GCM, per value). It is never stored: keep it — without it a restore yields placeholders instead of real secrets.',
    masterpassEmptyHint: 'Leave empty to pack explicit placeholders instead of secrets.',
    createStarted: 'Checkpoint running…',
    destinationInUseError: "'{name}' still holds checkpoints — delete them first.",
    origin: 'Origin',
    originManual: 'manual',
    originSchedule: 'scheduled',
    originPreRestore: 'pre-restore',
    originPreChange: 'pre-change',
    preChangeRetention: 'Pre-change checkpoints',
    preChangeRetentionHint:
      'Empty = disabled. The first configuration write after a period of quiet freezes the previous state here (origin pre-change); the newest are kept up to the count — its own, prunable retention.',
    diffTitle: 'Change preview',
    diffFailed: 'Preview failed',
    diffRetry: 'Retry',
    diffCounts: '{changed} to change · {missing} to create · {extra} extra · {equal} identical',
    diffChanged: 'changes',
    diffMissing: 'would be created',
    diffExtra: 'would be deleted',
    diffJsonb: 'jsonb references not verifiable before the apply',
    diffSecret: 'sealed fields, not comparable',
    diffMore: '{n} more…',
    diffNeeded: 'The start arms once the preview mirrors the selection.',
    phase: 'State',
    phaseRunning: 'running',
    phaseComplete: 'complete',
    phaseError: 'error',
    secrets: 'Secrets',
    secretsMasterpass: 'masterpass',
    secretsPlaceholders: 'placeholders',
    size: 'Size',
    created: 'Created',
    destination: 'Destination',
    download: 'Download .tvbk',
    deleteCheckpoint: 'Delete checkpoint',
    noCheckpoints: 'No checkpoints yet — create one from a destination.',

    // Round #156 (dialog standard): the checkpoint record's full read surface —
    // detail rows for the fields the table never showed.
    families: 'Families',
    generation: 'Generation',
    completedAt: 'Completed at',
    errorDetail: 'Error detail',
    manifest: 'Manifest',
    restoreHint:
      'Upload a .tvbk artifact: the explorer shows what it carries, then you choose what to restore and how.',
    uploadFile: 'Upload a .tvbk file',
    uploading: 'Uploading…',
    uploadFailed: 'The artifact could not be opened',
    explorerTitle: 'Checkpoint contents',
    checkpointFrom: 'Checkpoint of cluster {cluster}, taken {when} ({origin}, generation {generation})',
    otherCluster: 'This checkpoint was taken on ANOTHER cluster ({cluster}) — cross-cluster restore is a future round.',
    encrypted: 'encrypted',
    objectsCount: '{n} objects',
    mode: 'Mode',
    modeMerge: 'Merge — reconcile: upsert the selected rows, delete nothing',
    modeOverwrite:
      'Overwrite — the checkpoint wins: a fully selected family is replaced whole (current rows not in the checkpoint are removed)',
    restoreMasterpassHint:
      'The masterpass this checkpoint was taken with. Without it, encrypted secrets are restored as explicit placeholders.',
    checkMasterpass: 'Check',
    masterpassOk: 'Masterpass correct',
    masterpassWrong: 'Wrong masterpass',
    parachuteDestination: 'Parachute destination',
    parachuteHint: 'Before touching any row, the task takes an automatic pre-restore checkpoint here.',
    summaryTitle: 'What will happen',
    summaryLine: '{family}: {what} — {mode}',
    summaryWhole: 'whole family',
    summaryObjects: '{n} selected objects',
    confirmHint: 'Type the cluster name ({name}) to confirm — a restore rewrites the declared configuration.',
    confirmField: 'Cluster name',
    startRestore: 'Restore',
    restoreRunning: 'Restore running…',
    restoreDone: 'Restore complete — the reconcilers converge on the new declaration.',
    restoreFailed: 'The restore failed',
    discard: 'Discard',
    selectionEmpty: 'Select at least one family or object.'
  },

  // Backup dei DATI di VM e container (#235) — SCHELETRO 2026-08-07.
  backup: {
    intro: {
      title: 'Backup of VM and container data',
      subtitle: 'The building blocks external backup software uses',
      whatItIs:
        'TVirt exposes four primitives: quiesced snapshots, per-disk change tracking, read-only NBD export and configuration export. Veeam, Bacula, Acronis and friends drive them.',
      whatItIsNot:
        'TVirt is not a backup product: it schedules nothing, retains nothing and stores no archive of your data. Retention, catalogs and restore policies belong to the software you already use.'
    },
    cbt: {
      title: 'Change tracking',
      colVm: 'VM',
      colDisk: 'Disk',
      colState: 'State',
      colBitmaps: 'Bitmaps',
      colObservedAt: 'Observed',
      colChangeId: 'Change ID',
      colMechanic: 'Mechanic',
      stateActive: 'Active',
      stateInactive: 'Off',
      stateInconsistent: 'Inconsistent — next backup must be full',
      stateBroken: 'Chain broken — disable, then enable as a new chain',
      stateSnapshots: 'Always on — delta between snapshots',
      stateUnavailable: 'Not available on this disk',
      mechanic: {
        'qcow2-bitmap': 'qcow2 bitmap',
        'rbd-snapdiff': 'Ceph snapshot diff',
        'lvm-thin-delta': 'Thin pool delta',
        none: 'None'
      },
      empty: 'No VM disk. Change tracking is read per disk: enable it on a qcow2 disk, or take snapshots on rbd/SAN.',
      chainBroken: 'Chain broken',
      chainBrokenText:
        'Severed by a {reason}: {bitmaps}. Disable the bitmap (the chain is gone, the next backup is full), then enable it as a new chain.',
      lineages: 'Epochs',
      epochFrozen: 'frozen in snapshot {snapshot}',
      epochLost: 'lost ({cause})',
      epochRecording: 'recording on the head',
      milestones: 'Snapshot milestones',
      enable: 'Enable change tracking',
      disable: 'Disable',
      reset: 'Reset',
      changedAreas: 'Changed areas',
      denied: 'You lack the permission on this VM',
      enableTitle: 'Enable change tracking',
      disableTitle: 'Disable change tracking',
      resetTitle: 'Reset the bitmap',
      areasTitle: 'Changed areas',
      bitmapName: 'Bitmap name',
      bitmapNameHint: 'Your own name: tvirt- and tvbk- belong to the platform',
      granularity: 'Granularity (bytes)',
      granularityHint: 'A power of two; 65536 by default',
      bitmap: 'Bitmap',
      disableHint: 'Removes the bitmap and its whole epoch history: the next backup must be full.',
      resetHint: 'Empties the bitmap — a new baseline: a change ID older than the reset is no longer composable.',
      areasFrom: 'From snapshot',
      areasTo: 'To snapshot',
      areasRun: 'Compute',
      areasResult: '{count} changed areas, {bytes}',
      areasEmpty: 'No changed area between the two snapshots',
      areasZero: 'zeros',
      areasMore: 'first {shown} of {count} shown',
      submit: 'Confirm',
      error: 'The change-tracking action failed'
    },
    exports: {
      title: 'Open NBD exports',
      colVm: 'VM',
      colDisk: 'Disk',
      colSnapshot: 'Snapshot',
      colBitmap: 'Bitmap',
      colPhase: 'Phase',
      colNode: 'Node',
      colEndpoint: 'Endpoint',
      colExpires: 'Expires',
      colCreated: 'Opened',
      colError: 'Error',
      phase: {
        preparing: 'Preparing',
        ready: 'Ready',
        closed: 'Closed',
        error: 'Failed'
      },
      expiresIn: 'in {minutes} min',
      expired: 'expired',
      empty: 'No export. A read session serves one disk at one snapshot to an NBD client; a write session fills a virgin disk of a stopped VM. Both die at expiry.',
      endpointHint: 'TLS with the cluster CA; the export name is the ticket — it dies with the session',
      closeDenied: 'You lack the permission to close exports on this VM',
      open: 'Open NBD export',
      openTitle: 'Open an NBD export',
      openDisk: 'Disk',
      openBitmap: 'Bitmap (optional)',
      openBitmapHint:
        "Publishes that bitmap's epoch frozen in this snapshot as the dirty-extent meta-context (qcow2 disks only)",
      openTtl: 'Lifetime (minutes)',
      openSubmit: 'Open',
      openError: 'The export could not be opened',
      colMode: 'Mode',
      mode: {
        read: 'Read',
        write: 'Write'
      },
      caCertificate: 'Cluster CA',
      caCopy: 'Copy PEM',
      caDownload: 'Download ca-cert.pem',
      caHint: 'Write the PEM to ca-cert.pem in a private directory and connect with nbds://…?tls-certificates=<dir> (nbdcopy, nbdinfo) or --object tls-creds-x509,dir=<dir>,endpoint=client (qemu-img)',
      caAbsent: 'The session carries no CA (no PKI)',
      openWrite: 'Open write export',
      openWriteTitle: 'Open a write export',
      openWriteIntro: 'The disk must be virgin: born empty, never written, no snapshot; the VM stopped. The restore FILLS the disk, it does not repair it. While the session is open the VM cannot start, snapshot or clone and the disk cannot move.',
      openWriteSubmit: 'Open',
      openWriteError: 'The write export could not be opened',
      openWriteDenied: 'You lack vm.restore.data on this VM',
      openWriteStopped: 'Stop the VM to open a write export',
      fromDescription: 'Create VM from description',
      fromDescriptionTitle: 'Create a VM from a description',
      fromDescriptionIntro: 'The export-vm-meta description comes back as a stopped VM with empty disks of the described geometry — ready for the write exports, disk by disk. The bytes do not pass through here: restore-disk (CLI) or the backup product\'s NBD client carries them.',
      fromDescriptionDoc: 'Description (JSON)',
      fromDescriptionDocHint: 'Paste the export-vm-meta document, or pick the file',
      fromDescriptionFile: 'Pick file…',
      fromDescriptionInvalid: 'The text is not a JSON document',
      fromDescriptionName: 'New VM name',
      fromDescriptionScope: 'Scope',
      fromDescriptionScopeHome: 'Your home scope',
      fromDescriptionStorage: 'Disk storage',
      fromDescriptionStorageHint: 'Every disk is born here, in the storage\'s default format',
      fromDescriptionMetadataStorage: 'Metadata storage',
      fromDescriptionSameAsStorage: 'Same as the disks',
      fromDescriptionBridge: 'Bridge',
      fromDescriptionBridgeHint: 'Without a bridge or a network the VM is born with no NIC',
      fromDescriptionNetwork: 'Logical network (fabric/network)',
      fromDescriptionSubmit: 'Create',
      fromDescriptionError: 'The VM could not be created',
      fromDescriptionDone: 'VM created: {name}',
      fromDescriptionOpen: 'Open the VM',
      fromDescriptionDenied: 'You lack vm.create',
      fromDescriptionStillRunning: 'The creation is still running: waiting on'
    },

    // Il MOTORE integrato (#28) — i backup dei dati che TVirt fa davvero.
    vm: {
      title: 'VM data backups',
      titleOnVm: 'Data backups',
      empty:
        'No backups. A backup copies the disks, the UEFI varstore and the vTPM state onto a destination, and outlives the VM.',
      colVm: 'VM',
      colCreated: 'Taken',
      colPhase: 'Outcome',
      colDestination: 'Destination',
      colContains: 'Contents',
      colSize: 'Size',
      colOrigin: 'Origin',
      colMode: 'Type',
      retentionChip: 'Retention',
      retentionHint:
        "The destination's retention can delete this backup without asking: it was born of a schedule. Manual backups are never swept.",
      policyRemoved: 'Policy removed: {name}',
      policyRemovedHint:
        'The policy that scheduled this backup no longer exists: no retention governs it (never pruned automatically) — delete it by hand when it is no longer needed.',
      phase: {
        running: 'Running',
        complete: 'Complete',
        error: 'Failed'
      },
      origin: {
        manual: 'Manual',
        schedule: 'Scheduled'
      },
      mode: {
        full: 'Full',
        incremental: 'Incremental'
      },
      contains: {
        disks: 'Disks',
        varstore: 'UEFI varstore',
        vtpm: 'vTPM state',
        snapshots: 'Snapshots'
      },
      vmState: {
        stopped: 'VM stopped',
        running: 'VM running (hot)'
      },
      artifactClass: { metadata: 'Metadata', data: 'Data', secret: 'Secret' },
      detail: {
        tabOverview: 'Overview',
        tabArtefact: 'Artefact',
        id: 'Identifier',
        scope: 'Scope',
        vmState: 'VM state at copy time',
        pointInTime: 'Data as of',
        completedAt: 'Settled',
        node: 'Node that copied',
        error: 'Error',
        path: 'Directory on the storage',
        checksum: 'manifest sha256',
        secretsExposed: 'Sensitive material in the clear',
        secretsExposedYes: 'Yes — the destination does not encrypt',
        secretsExposedNo: 'No',
        baseBackup: 'Base backup',
        tabManifest: 'Manifest',
        manifestHint:
          "The artefact's cleartext index: one file per row with its sha256. The directory verifies without TVirt — qemu-img, tar and jq are enough.",
        manifestEmpty: 'The manifest lists no files.',
        manifestError: 'The manifest cannot be read',
        colEntry: 'File',
        colClass: 'Class',
        colSha: 'sha256'
      },
      create: {
        action: 'Back up now',
        title: 'Back up now',
        submit: 'Start the backup',
        destination: 'Destination',
        destinationHint: 'Only destinations that accept backups.',
        noDestinations: 'No destination accepts backups: create one under System → Checkpoints.',
        contentsHint:
          'Every disk travels, plus the UEFI varstore and the vTPM state. The last two are the point: a sealed guest must boot again.',
        error: 'The backup did not start',
        stillRunning: 'Backup still running — the copy carries on even if you close this.',
        vmRunning:
          'The VM is {phase}: a backup needs a settled VM — stopped for the cold copy, running for the hot one.'
      },
      restore: {
        action: 'Restore',
        title: 'Restore',
        intro:
          'A restore is irreversible. Choose where the data goes back: into a brand-new machine, or over the one it came from.',
        modeNewVm: 'Restore into a NEW VM',
        modeNewVmHint:
          'Touches nothing that exists — this is the option you verify a backup with. New identifier and freshly minted MACs.',
        modeOver: 'Overwrite the VM it came from',
        modeOverHint: "Destroys the target VM's current data, with no way back. The VM must be stopped.",
        newName: 'New VM name',
        newNameHint: 'It must be free in the target scope.',
        storage: 'Storage for the disks',
        storageHint: "Required: the manifest records a disk's size and format, never the storage it used to live on.",
        metadataStorage: 'Storage for varstore and vTPM',
        metadataStorageHint: 'Where the UEFI identity and the vTPM state land.',
        sameAsStorage: 'Same as the disks',
        bridge: 'Host bridge',
        bridgeHint:
          'Every recorded NIC comes back on this bridge with a fresh MAC. Left empty, the VM is born with no NICs — the safer default.',
        overwriteTitle: 'You are about to overwrite this VM',
        overwriteName: 'Name',
        overwriteId: 'Identifier',
        overwriteDisks: 'Disks that will be rewritten',
        overwriteNoDisks: 'No disks.',
        overwriteKeeps:
          'Identifier, name, scope, NICs and MACs stay untouched: a backup is the DATA, the configuration is the checkpoint.',
        confirmField: 'VM name',
        confirmHint: 'Type {name} to confirm.',
        targetLoading: 'Reading the target VM…',
        targetRunning:
          'The target VM is {phase}: a restore rewrites the bytes under the guest, so it wants it stopped.',
        targetGone: 'The original VM can no longer be read: restoring into a new VM is still available.',
        onlyComplete: 'The backup is {phase}: only a complete backup can restore.',
        submitNewVm: 'Restore into the new VM',
        submitOver: 'Overwrite',
        error: 'The restore did not start',
        stillRunning: 'Restore still running — copying whole disks takes time.'
      }
    },
    guest: {
      title: {
        ct: 'Container data backups',
        app: 'App data backups'
      },
      tabTitle: 'Backups',
      empty: 'No backups. A backup copies the persistent data onto a destination, and outlives its object.',
      colSubject: {
        ct: 'Container',
        app: 'App'
      },
      colCreated: 'Taken',
      colPhase: 'Outcome',
      colDestination: 'Destination',
      colContains: 'Contents',
      colSize: 'Size',
      colOrigin: 'Origin',
      policyRemoved: 'Policy removed: {name}',
      policyRemovedHint:
        'The policy that scheduled this backup no longer exists: no retention governs it (never pruned automatically) — delete it by hand when it is no longer needed.',
      phase: {
        running: 'Running',
        complete: 'Complete',
        error: 'Failed'
      },
      origin: {
        manual: 'Manual',
        schedule: 'Scheduled'
      },
      state: {
        stopped: 'Object stopped',
        running: 'Object running (frozen)'
      },
      contains: {
        rootfs: 'Rootfs tree',
        config: 'Configuration',
        volumes: 'Persistent volumes',
        root: 'Persistent root'
      },
      artifactClass: {
        metadata: 'Metadata',
        data: 'Data',
        secret: 'Secret'
      },
      create: {
        action: 'Back up now',
        title: 'New backup',
        submit: 'Start the backup',
        error: 'The backup did not start',
        destination: 'Destination',
        destinationHint: 'Only destinations that accept data backups.',
        noDestinations: 'No destination accepts data backups: add “Data backups” to a destination.',
        hint: {
          ct: 'The whole rootfs tree travels, faithfully (numeric ownership, xattrs, ACLs), plus the declared configuration in the manifest. A running container is FROZEN for the whole pack: crash-consistent copy, declared.',
          app: 'ONLY the persistent volumes (and the persistent root) travel, plus the configuration in the manifest. Ephemeral pieces are excluded by ratification and the manifest names them; env values declared secret do not travel.'
        },
        stillRunning: 'Backup still running — packing whole trees takes time.'
      },
      detail: {
        tabOverview: 'Overview',
        tabManifest: 'Manifest',
        id: 'Identifier',
        scope: 'Scope',
        state: 'State at copy time',
        pointInTime: 'Data as of',
        completedAt: 'Settled',
        node: 'Node that copied',
        error: 'Error',
        path: 'Directory on the storage',
        checksum: 'manifest sha256',
        manifestHint:
          "The artefact's index: one entry per file with its sha256 — without the hashes “the backup is there” is an act of faith.",
        manifestError: 'The manifest cannot be read',
        manifestEmpty: 'No artefacts in the manifest.',
        colEntry: 'File',
        colClass: 'Class',
        colSha: 'sha256',
        excluded: 'Excluded by ratification (does not travel)',
        secretKeys: 'Secret env keys omitted (#307) — re-enter them after a restore'
      },
      restore: {
        action: 'Restore',
        title: 'Restore',
        submit: 'Restore',
        error: 'The restore did not start',
        stillRunning: 'Restore still running — extracting whole trees takes time.',
        onlyComplete: 'The backup is {phase}: only a complete backup can restore.',
        asNew: {
          ct: 'Into a NEW container — destroys nothing: the gesture you check a backup with',
          app: 'Into a NEW App — destroys nothing: the gesture you check a backup with'
        },
        over: {
          ct: 'OVER the source container — the whole rootfs tree is replaced',
          app: "OVER the source App — every persistent volume's contents are replaced"
        },
        name: 'Name of the new object',
        storage: 'Storage',
        storageHint: 'Where the restored data lands — the manifest never records the pool that was lost.',
        storageDeclared: 'The storages declared in the manifest',
        bridge: 'Host bridge',
        bridgeHint:
          'Every recorded NIC comes back on this bridge with a fresh MAC. Empty = born unattached, the safer default.',
        node: 'Node',
        nodeHint: 'The member the App is reborn on: the node is cluster identity and never comes from the manifest.',
        secretKeysWarning:
          'Env values declared secret do not travel in the backup: the reborn copy wants them re-entered (the task log names the keys).',
        overInventory: 'You are about to overwrite this object',
        confirmType: 'Type {name} to confirm',
        targetGone: 'The source object can no longer be read: restoring as new is still available.',
        targetLoading: 'Reading the target object…',
        targetRunning:
          'The target object is running: a restore replaces the bytes under the guest, so it wants it stopped.'
      }
    }
  }
}

export const itIT: typeof enUS = {
  ckpt: {
    contents: 'Accetta',
    contentsHint:
      'Cosa può atterrare qui. «Checkpoint» è la configurazione del cluster, «Backup dati» sono i backup delle VM: le due letture si compongono, una destinazione può accettarle entrambe.',
    content: { checkpoints: 'Checkpoint', backups: 'Backup dati' },
    destinationsTitle: 'Destinazioni di backup',
    checkpointsTitle: 'Checkpoint',
    restoreTitle: 'Ripristino',
    addDestination: 'Aggiungi destinazione',
    addDestinationTitle: 'Aggiungi destinazione di backup',
    editDestinationTitle: 'Modifica destinazione',
    storage: 'Storage',
    storageHint:
      'Uno storage dir-like (dir/NFS/CIFS/CephFS) — gli artefatti sono file normali sotto il namespace della piattaforma.',
    schedule: 'Pianificazione',
    scheduleNone: 'Solo manuale',
    scheduleHourly: 'Ogni ora',
    scheduleDaily: 'Giornaliera',
    scheduleWeekly: 'Settimanale',
    scheduleHour: 'Ora',
    scheduleWeekday: 'Giorno',
    scheduleHint:
      'I checkpoint pianificati non hanno un operatore che digita la masterpass: i segreti del keystore vengono impacchettati come placeholder.',
    retention: 'Retention',
    retentionHint:
      'Quanti checkpoint pianificati tenere — lo sweeper rimuove i più vecchi oltre il conteggio. I checkpoint manuali e pre-ripristino non vengono mai rimossi.',
    paused: 'In pausa',
    pausedHint:
      'Il blackout dell’operatore: finché è in pausa, su questa destinazione non si pianifica e non si pota niente — policy dati e checkpoint. I giri in corso non vengono mai toccati.',
    orphanRuns: '{n} giri orfani',
    orphanRunsHint:
      'Giri pianificati di policy che non esistono più su questa destinazione: nessuna ritenzione li governa — mai contati né potati — e si cancellano solo a mano. La lente «backups» di tvirt diagnose nomina le policy rimosse.',
    rescan: 'Riadotta artefatti',
    rescanTitle: 'Riadozione artefatti di {name}',
    rescanHint:
      'Ricostruisce il catalogo dei backup (VM/CT/App) dai manifest degli artefatti presenti sulla destinazione. Le righe già presenti non vengono toccate; i backup di policy sconosciute restano orfani (mai potati automaticamente). È il gesto dopo un ripristino di bootstrap.',
    masterpassDrWarning:
      'Scenario disastro: i dischi LUKS tornano SOLO da un checkpoint CON masterpass. I checkpoint pianificati impacchettano segnaposto — prendi periodicamente un checkpoint manuale con masterpass.',
    policy: {
      title: 'Policy di backup dei dati',
      hint: 'Policy nominate: quali oggetti (kind, tag, scope), ogni quanto, quando un giro può partire, e quante copie tenere. Il gesto di arruolamento resta al proprietario dell’oggetto: i tag si assegnano sul suo scope.',
      add: 'Aggiungi policy',
      column: 'Policy dati',
      name: 'Nome',
      nameHint:
        'Etichetta della policy, unica nella destinazione. I giri seguono l’id stabile della policy: rinominare è gratis; cancellarla rende orfani i suoi giri (non più contati né potati).',
      kindLocked: 'La famiglia di oggetti di una policy esistente non si cambia: crea una policy nuova.',
      kindLabel: 'Oggetti',
      kind: { vm: 'Macchine virtuali', ct: 'Container', app: 'App' },
      tags: 'Selettore a tag',
      tagsHint: 'Coppie tag=valore separate da virgola, in AND. Tag e scope vuoti selezionano niente — mai «tutto».',
      scopes: 'Scope',
      scopesHint: 'Percorsi di scope (sottoalberi), separati da virgola, in OR tra loro.',
      windowStart: 'Finestra da',
      windowEnd: 'Finestra a',
      windowHint:
        'Finestra di esecuzione (HH:MM, ora locale; inizio oltre la fine scavalca la mezzanotte). Governa solo l’AVVIO: un giro che la sfora non viene mai ucciso.',
      mode: 'Modalità',
      modeHint: 'L’incrementale cavalca la famiglia di serie; una serie rotta degrada a full, detto nel giornale.',
      modeFull: 'Full',
      modeIncremental: 'Incrementale',
      requireQuiesce: 'Richiedi quiesce (guest agent)',
      paused: 'Policy in pausa',
      retentionNone: 'Tieni tutto',
      retentionCount: 'Conteggio',
      retentionGfs: 'Anelli GFS',
      gfs: { keepLast: 'Ultimi', daily: 'Giornalieri', weekly: 'Settimanali', monthly: 'Mensili', yearly: 'Annuali' },
      gfsHint:
        'Per ogni anello si tiene il backup più nuovo di ciascuno degli ultimi N periodi. Un backup tenuto tiene viva la sua catena intera — mai una base con orfani.',
      lastRun: 'Ultimo giro',
      neverRan: 'mai eseguito'
    },
    lastCheckpoint: 'Ultimo checkpoint',
    neverRun: 'Mai creato',
    createNow: 'Crea checkpoint',
    createNowTitle: 'Crea checkpoint — {name}',
    masterpass: 'Masterpass',
    masterpassHint:
      'Protegge i valori segreti del keystore (argon2id → AES-256-GCM, per valore). Non viene mai salvata: conservala — senza, un ripristino produce placeholder al posto dei segreti veri.',
    masterpassEmptyHint: 'Lascia vuoto per impacchettare placeholder espliciti al posto dei segreti.',
    createStarted: 'Checkpoint in corso…',
    destinationInUseError: "'{name}' contiene ancora checkpoint — eliminali prima.",
    origin: 'Origine',
    originManual: 'manuale',
    originSchedule: 'pianificato',
    originPreRestore: 'pre-ripristino',
    originPreChange: 'pre-modifica',
    preChangeRetention: 'Checkpoint pre-modifica',
    preChangeRetentionHint:
      'Vuoto = disattivato. Il primo write di configurazione dopo un periodo di quiete fotografa qui lo stato precedente (origine pre-modifica); si tengono i più recenti fino al conteggio — retention propria, potabile.',
    diffTitle: 'Anteprima delle modifiche',
    diffFailed: 'Anteprima non riuscita',
    diffRetry: 'Riprova',
    diffCounts: '{changed} da modificare · {missing} da creare · {extra} in eccesso · {equal} identici',
    diffChanged: 'cambia',
    diffMissing: 'verrebbe creato',
    diffExtra: 'verrebbe eliminato',
    diffJsonb: 'riferimenti jsonb non verificabili prima dell’apply',
    diffSecret: 'campi sigillati non confrontabili',
    diffMore: 'altri {n}…',
    diffNeeded: 'L’avvio si arma quando l’anteprima rispecchia la selezione.',
    phase: 'Stato',
    phaseRunning: 'in corso',
    phaseComplete: 'completo',
    phaseError: 'errore',
    secrets: 'Segreti',
    secretsMasterpass: 'masterpass',
    secretsPlaceholders: 'placeholder',
    size: 'Dimensione',
    created: 'Creato',
    destination: 'Destinazione',
    download: 'Scarica .tvbk',
    deleteCheckpoint: 'Elimina checkpoint',
    noCheckpoints: 'Nessun checkpoint — creane uno da una destinazione.',

    // Round #156 (dialog standard): la superficie di lettura completa del
    // record di checkpoint — righe di dettaglio per i campi mai in tabella.
    families: 'Famiglie',
    generation: 'Generazione',
    completedAt: 'Completato il',
    errorDetail: 'Dettaglio errore',
    manifest: 'Manifest',
    restoreHint: "Carica un artefatto .tvbk: l'explorer mostra cosa contiene, poi scegli cosa ripristinare e come.",
    uploadFile: 'Carica un file .tvbk',
    uploading: 'Caricamento…',
    uploadFailed: "Impossibile aprire l'artefatto",
    explorerTitle: 'Contenuto del checkpoint',
    checkpointFrom: 'Checkpoint del cluster {cluster}, creato il {when} ({origin}, generazione {generation})',
    otherCluster:
      'Questo checkpoint proviene da un ALTRO cluster ({cluster}) — il ripristino cross-cluster arriva in un round futuro.',
    encrypted: 'cifrato',
    objectsCount: '{n} oggetti',
    mode: 'Modalità',
    modeMerge: 'Merge — riconcilia: upsert dei selezionati, non cancella nulla',
    modeOverwrite:
      'Overwrite — vince il checkpoint: una famiglia interamente selezionata è sostituita per intero (le righe correnti assenti dal checkpoint muoiono)',
    restoreMasterpassHint:
      'La masterpass con cui è stato creato il checkpoint. Senza, i segreti cifrati vengono ripristinati come placeholder espliciti.',
    checkMasterpass: 'Verifica',
    masterpassOk: 'Masterpass corretta',
    masterpassWrong: 'Masterpass errata',
    parachuteDestination: 'Destinazione paracadute',
    parachuteHint: 'Prima di toccare qualunque riga, il task crea qui un checkpoint automatico pre-ripristino.',
    summaryTitle: 'Cosa succederà',
    summaryLine: '{family}: {what} — {mode}',
    summaryWhole: 'famiglia intera',
    summaryObjects: '{n} oggetti selezionati',
    confirmHint:
      'Digita il nome del cluster ({name}) per confermare — un ripristino riscrive la configurazione dichiarata.',
    confirmField: 'Nome del cluster',
    startRestore: 'Ripristina',
    restoreRunning: 'Ripristino in corso…',
    restoreDone: 'Ripristino completato — i reconciler convergono sulla nuova dichiarazione.',
    restoreFailed: 'Il ripristino è fallito',
    discard: 'Scarta',
    selectionEmpty: 'Seleziona almeno una famiglia o un oggetto.'
  },

  // Backup dei DATI di VM e container (#235) — SCHELETRO 2026-08-07.
  backup: {
    intro: {
      title: 'Backup dei dati di VM e container',
      subtitle: 'I mattoni che i software di backup esterni usano',
      whatItIs:
        'TVirt espone quattro primitive: snapshot quiesced, change tracking per disco, export NBD in sola lettura ed export della configurazione. A guidarle sono Veeam, Bacula, Acronis e simili.',
      whatItIsNot:
        'TVirt non è un prodotto di backup: non pianifica, non applica ritenzione e non conserva alcun archivio dei tuoi dati. Ritenzione, cataloghi e politiche di ripristino restano al software che già usi.'
    },
    cbt: {
      title: 'Change tracking',
      colVm: 'VM',
      colDisk: 'Disco',
      colState: 'Stato',
      colBitmaps: 'Bitmap',
      colObservedAt: 'Osservato',
      colChangeId: 'Change ID',
      colMechanic: 'Meccanica',
      stateActive: 'Attivo',
      stateInactive: 'Non attivo',
      stateInconsistent: "Incoerente — il prossimo backup dev'essere completo",
      stateBroken: 'Catena interrotta — disabilita, poi riabilita come catena nuova',
      stateSnapshots: 'Sempre attivo — delta fra due snapshot',
      stateUnavailable: 'Non disponibile su questo disco',
      mechanic: {
        'qcow2-bitmap': 'Bitmap qcow2',
        'rbd-snapdiff': 'Diff fra snapshot Ceph',
        'lvm-thin-delta': 'Delta del thin pool',
        none: 'Nessuna'
      },
      empty:
        'Nessun disco di VM. Il change tracking si legge per disco: abilitalo su un disco qcow2, o prendi snapshot su rbd/SAN.',
      chainBroken: 'Catena interrotta',
      chainBrokenText:
        'Recisa da un {reason}: {bitmaps}. Disabilita la bitmap (la catena è persa, il prossimo backup è completo), poi riabilitala come catena nuova.',
      lineages: 'Epoche',
      epochFrozen: 'congelata nello snapshot {snapshot}',
      epochLost: 'persa ({cause})',
      epochRecording: 'in registrazione sulla testa',
      milestones: 'Milestone (snapshot)',
      enable: 'Abilita il change tracking',
      disable: 'Disabilita',
      reset: 'Azzera',
      changedAreas: 'Aree cambiate',
      denied: 'Non hai il permesso su questa VM',
      enableTitle: 'Abilita il change tracking',
      disableTitle: 'Disabilita il change tracking',
      resetTitle: 'Azzera la bitmap',
      areasTitle: 'Aree cambiate',
      bitmapName: 'Nome della bitmap',
      bitmapNameHint: 'Un nome tuo: tvirt- e tvbk- sono della piattaforma',
      granularity: 'Granularità (byte)',
      granularityHint: 'Una potenza di due; 65536 per default',
      bitmap: 'Bitmap',
      disableHint: "Rimuove la bitmap e tutta la sua storia di epoche: il prossimo backup dev'essere completo.",
      resetHint: "Svuota la bitmap — una baseline nuova: un change ID anteriore all'azzeramento non è più componibile.",
      areasFrom: 'Dallo snapshot',
      areasTo: 'Allo snapshot',
      areasRun: 'Calcola',
      areasResult: '{count} aree cambiate, {bytes}',
      areasEmpty: 'Nessuna area cambiata fra i due snapshot',
      areasZero: 'zeri',
      areasMore: 'mostrate le prime {shown} di {count}',
      submit: 'Conferma',
      error: 'Azione sul change tracking non riuscita'
    },
    exports: {
      title: 'Export NBD aperti',
      colVm: 'VM',
      colDisk: 'Disco',
      colSnapshot: 'Snapshot',
      colBitmap: 'Bitmap',
      colPhase: 'Fase',
      colNode: 'Nodo',
      colEndpoint: 'Endpoint',
      colExpires: 'Scade',
      colCreated: 'Aperto',
      colError: 'Errore',
      phase: {
        preparing: 'In preparazione',
        ready: 'Pronto',
        closed: 'Chiuso',
        error: 'Fallito'
      },
      expiresIn: 'fra {minutes} min',
      expired: 'scaduto',
      empty: 'Nessun export. Una sessione in lettura serve un disco a uno snapshot a un client NBD; una in scrittura riempie un disco vergine di una VM spenta. Entrambe muoiono alla scadenza.',
      endpointHint: "TLS con la CA del cluster; il nome dell'export è il ticket — muore con la sessione",
      closeDenied: 'Non hai il permesso di chiudere export su questa VM',
      open: 'Apri export NBD',
      openTitle: 'Apri un export NBD',
      openDisk: 'Disco',
      openBitmap: 'Bitmap (facoltativa)',
      openBitmapHint:
        "Pubblica l'epoca di quella bitmap congelata in questo snapshot come meta-context degli extent sporchi (solo dischi qcow2)",
      openTtl: 'Durata (minuti)',
      openSubmit: 'Apri',
      openError: "L'export non è stato aperto",
      colMode: 'Modalità',
      mode: {
        read: 'Lettura',
        write: 'Scrittura'
      },
      caCertificate: 'CA del cluster',
      caCopy: 'Copia PEM',
      caDownload: 'Scarica ca-cert.pem',
      caHint: 'Scrivi il PEM in ca-cert.pem dentro una cartella privata e connetti con nbds://…?tls-certificates=<cartella> (nbdcopy, nbdinfo) o --object tls-creds-x509,dir=<cartella>,endpoint=client (qemu-img)',
      caAbsent: 'La sessione non porta la CA (nessuna PKI)',
      openWrite: 'Apri export in scrittura',
      openWriteTitle: 'Apri un export in scrittura',
      openWriteIntro: 'Il disco deve essere vergine: nato vuoto, mai scritto, senza snapshot; la VM spenta. Il ripristino RIEMPIE il disco, non lo ripara. Finché la sessione è aperta la VM non si avvia, non si snapshotta, non si clona e il disco non si sposta.',
      openWriteSubmit: 'Apri',
      openWriteError: 'L\'export in scrittura non è stato aperto',
      openWriteDenied: 'Non hai il permesso vm.restore.data su questa VM',
      openWriteStopped: 'Spegni la VM per aprire un export in scrittura',
      fromDescription: 'Crea VM da description',
      fromDescriptionTitle: 'Crea una VM da una description',
      fromDescriptionIntro: 'La description di export-vm-meta torna come VM spenta coi dischi vuoti della geometria descritta — pronta per gli export in scrittura, disco per disco. I byte non passano da qui: li porta restore-disk (CLI) o il client NBD del prodotto di backup.',
      fromDescriptionDoc: 'Description (JSON)',
      fromDescriptionDocHint: 'Incolla il documento di export-vm-meta, o scegli il file',
      fromDescriptionFile: 'Scegli file…',
      fromDescriptionInvalid: 'Il testo non è un documento JSON',
      fromDescriptionName: 'Nome della VM nuova',
      fromDescriptionScope: 'Scope',
      fromDescriptionScopeHome: 'Lo scope di partenza',
      fromDescriptionStorage: 'Storage dei dischi',
      fromDescriptionStorageHint: 'Ogni disco nasce qui, nel formato di default dello storage',
      fromDescriptionMetadataStorage: 'Storage dei metadati',
      fromDescriptionSameAsStorage: 'Lo stesso dei dischi',
      fromDescriptionBridge: 'Bridge',
      fromDescriptionBridgeHint: 'Senza bridge né rete la VM nasce senza schede',
      fromDescriptionNetwork: 'Rete logica (fabric/rete)',
      fromDescriptionSubmit: 'Crea',
      fromDescriptionError: 'La VM non è stata creata',
      fromDescriptionDone: 'VM creata: {name}',
      fromDescriptionOpen: 'Apri la VM',
      fromDescriptionDenied: 'Non hai il permesso vm.create',
      fromDescriptionStillRunning: 'La creazione è ancora in corso: si continua ad aspettare'
    },

    // Il MOTORE integrato (#28) — i backup dei dati che TVirt fa davvero.
    vm: {
      title: 'Backup dei dati delle VM',
      titleOnVm: 'Backup dei dati',
      empty:
        'Nessun backup. Un backup copia i dischi, il varstore UEFI e lo stato del vTPM su una destinazione, e sopravvive alla VM.',
      colVm: 'VM',
      colCreated: 'Eseguito il',
      colPhase: 'Esito',
      colDestination: 'Destinazione',
      colContains: 'Contenuto',
      colSize: 'Dimensione',
      colOrigin: 'Origine',
      colMode: 'Tipo',
      retentionChip: 'Ritenzione',
      retentionHint:
        'La ritenzione della destinazione può cancellare questo backup senza chiedere: è nato da una pianificazione. I backup manuali non vengono mai spazzati.',
      policyRemoved: 'Policy rimossa: {name}',
      policyRemovedHint:
        'La policy che ha pianificato questo backup non esiste più: nessuna ritenzione lo governa (mai potato automaticamente) — si cancella a mano quando non serve più.',
      phase: {
        running: 'In corso',
        complete: 'Completato',
        error: 'Fallito'
      },
      origin: {
        manual: 'Manuale',
        schedule: 'Pianificato'
      },
      mode: {
        full: 'Completo',
        incremental: 'Incrementale'
      },
      contains: {
        disks: 'Dischi',
        varstore: 'Varstore UEFI',
        vtpm: 'Stato vTPM',
        snapshots: 'Snapshot'
      },
      vmState: {
        stopped: 'VM spenta',
        running: 'VM accesa (a caldo)'
      },
      artifactClass: { metadata: 'Metadati', data: 'Dati', secret: 'Segreto' },
      detail: {
        tabOverview: 'Riepilogo',
        tabArtefact: 'Artefatto',
        id: 'Identificativo',
        scope: 'Ambito',
        vmState: 'Stato della VM alla copia',
        pointInTime: 'Istante dei dati',
        completedAt: 'Concluso il',
        node: 'Nodo che ha copiato',
        error: 'Errore',
        path: "Cartella sull'archivio",
        checksum: 'sha256 del manifest',
        secretsExposed: 'Materiale sensibile in chiaro',
        secretsExposedYes: 'Sì — la destinazione non cifra',
        secretsExposedNo: 'No',
        baseBackup: 'Backup di base',
        tabManifest: 'Manifest',
        manifestHint:
          "L'indice in chiaro dell'artefatto: un file per riga con il suo sha256. La cartella si verifica anche senza TVirt — bastano qemu-img, tar e jq.",
        manifestEmpty: 'Il manifest non elenca file.',
        manifestError: 'Il manifest non è leggibile',
        colEntry: 'File',
        colClass: 'Classe',
        colSha: 'sha256'
      },
      create: {
        action: 'Esegui backup adesso',
        title: 'Esegui backup adesso',
        submit: 'Avvia il backup',
        destination: 'Destinazione',
        destinationHint: 'Solo le destinazioni che accettano i backup.',
        noDestinations: 'Nessuna destinazione accetta i backup: creane una in Sistema → Checkpoint.',
        contentsHint:
          'Viaggiano tutti i dischi, il varstore UEFI e lo stato del vTPM. Gli ultimi due sono il punto: un guest sigillato deve ripartire.',
        error: 'Il backup non è partito',
        stillRunning: 'Backup ancora in corso — la copia continua anche se chiudi.',
        vmRunning:
          'La VM è {phase}: il backup vuole una VM stabile — spenta per la copia a freddo, accesa per quella a caldo.'
      },
      restore: {
        action: 'Ripristina',
        title: 'Ripristina',
        intro:
          'Il ripristino è irreversibile. Scegli dove riportare i dati: in una macchina nuova, oppure sopra quella di partenza.',
        modeNewVm: 'Ripristina in una VM NUOVA',
        modeNewVmHint:
          "Non tocca niente di ciò che esiste — è l'opzione con cui si verifica un backup. Identificativo nuovo e MAC nuovi di zecca.",
        modeOver: 'Sovrascrivi la VM di partenza',
        modeOverHint: "Distrugge i dati attuali della VM bersaglio, senza ritorno. La VM dev'essere spenta.",
        newName: 'Nome della nuova VM',
        newNameHint: "Dev'essere libero nell'ambito di destinazione.",
        storage: 'Archivio dei dischi',
        storageHint:
          "Obbligatorio: il manifest registra dimensione e formato di un disco, mai l'archivio da cui veniva.",
        metadataStorage: 'Archivio di varstore e vTPM',
        metadataStorageHint: "Dove atterrano l'identità UEFI e lo stato del vTPM.",
        sameAsStorage: 'Lo stesso dei dischi',
        bridge: "Bridge dell'host",
        bridgeHint:
          'Ogni scheda registrata torna su questo bridge con un MAC nuovo. Lasciato vuoto, la VM nasce senza schede — ed è il default più sicuro.',
        overwriteTitle: 'Stai per sovrascrivere questa VM',
        overwriteName: 'Nome',
        overwriteId: 'Identificativo',
        overwriteDisks: 'Dischi che verranno riscritti',
        overwriteNoDisks: 'Nessun disco.',
        overwriteKeeps:
          'Restano intatti identificativo, nome, ambito, schede di rete e MAC: un backup sono i DATI, la configurazione è il checkpoint.',
        confirmField: 'Nome della VM',
        confirmHint: 'Digita {name} per confermare.',
        targetLoading: 'Lettura della VM bersaglio…',
        targetRunning:
          'La VM bersaglio è {phase}: il ripristino sovrascrive i byte sotto il guest, quindi la vuole spenta.',
        targetGone: 'La VM di partenza non è più leggibile: resta il ripristino in una VM nuova.',
        onlyComplete: 'Il backup è {phase}: solo un backup completato può ripristinare.',
        submitNewVm: 'Ripristina nella nuova VM',
        submitOver: 'Sovrascrivi',
        error: 'Il ripristino non è partito',
        stillRunning: 'Ripristino ancora in corso — copiare dischi interi richiede tempo.'
      }
    },
    guest: {
      title: {
        ct: 'Backup dei dati dei container',
        app: 'Backup dei dati delle App'
      },
      tabTitle: 'Backup',
      empty: 'Nessun backup. Un backup copia i dati persistenti su una destinazione, e sopravvive al suo oggetto.',
      colSubject: {
        ct: 'Container',
        app: 'App'
      },
      colCreated: 'Eseguito',
      colPhase: 'Esito',
      colDestination: 'Destinazione',
      colContains: 'Contenuto',
      colSize: 'Dimensione',
      colOrigin: 'Origine',
      policyRemoved: 'Policy rimossa: {name}',
      policyRemovedHint:
        'La policy che ha pianificato questo backup non esiste più: nessuna ritenzione lo governa (mai potato automaticamente) — si cancella a mano quando non serve più.',
      phase: {
        running: 'In corso',
        complete: 'Completato',
        error: 'Fallito'
      },
      origin: {
        manual: 'Manuale',
        schedule: 'Pianificato'
      },
      state: {
        stopped: 'Oggetto spento',
        running: 'Oggetto acceso (congelato)'
      },
      contains: {
        rootfs: 'Albero rootfs',
        config: 'Configurazione',
        volumes: 'Volumi persistent',
        root: 'Root persistente'
      },
      artifactClass: {
        metadata: 'Metadati',
        data: 'Dati',
        secret: 'Segreto'
      },
      create: {
        action: 'Esegui backup adesso',
        title: 'Nuovo backup',
        submit: 'Avvia il backup',
        error: 'Il backup non è partito',
        destination: 'Destinazione',
        destinationHint: 'Solo le destinazioni che accettano i backup di dati.',
        noDestinations: 'Nessuna destinazione accetta i backup di dati: aggiungi «Backup di dati» a una destinazione.',
        hint: {
          ct: "Viaggia l'intero albero rootfs, fedele (proprietari numerici, xattr, ACL), più la configurazione dichiarata nel manifest. Un container acceso viene CONGELATO per la durata del pacchetto: copia crash-consistent, dichiarata.",
          app: 'Viaggiano SOLO i volumi persistent (e la root persistente) più la configurazione nel manifest. Gli ephemeral sono esclusi per ratifica e il manifest li nomina; i valori env dichiarati segreti non viaggiano.'
        },
        stillRunning: 'Backup ancora in corso — impacchettare alberi interi richiede tempo.'
      },
      detail: {
        tabOverview: 'Panoramica',
        tabManifest: 'Manifest',
        id: 'Identificativo',
        scope: 'Ambito',
        state: 'Stato alla copia',
        pointInTime: "Dati all'istante",
        completedAt: 'Concluso',
        node: 'Nodo che ha copiato',
        error: 'Errore',
        path: 'Cartella sullo storage',
        checksum: 'sha256 del manifest',
        manifestHint:
          "L'indice dell'artefatto: una voce per file col suo sha256 — senza gli hash «il backup c'è» è un atto di fede.",
        manifestError: 'Il manifest non si legge',
        manifestEmpty: 'Nessun artefatto nel manifest.',
        colEntry: 'File',
        colClass: 'Classe',
        colSha: 'sha256',
        excluded: 'Escluso per ratifica (non viaggia)',
        secretKeys: 'Chiavi env segrete omesse (#307) — da reinserire dopo un ripristino'
      },
      restore: {
        action: 'Ripristina',
        title: 'Ripristino',
        submit: 'Ripristina',
        error: 'Il ripristino non è partito',
        stillRunning: 'Ripristino ancora in corso — estrarre alberi interi richiede tempo.',
        onlyComplete: 'Il backup è {phase}: solo un backup completato può ripristinare.',
        asNew: {
          ct: 'In un container NUOVO — non distrugge niente: è il gesto con cui si verifica un backup',
          app: "In un'App NUOVA — non distrugge niente: è il gesto con cui si verifica un backup"
        },
        over: {
          ct: "SOPRA il container di origine — l'intero albero rootfs viene sostituito",
          app: "SOPRA l'App di origine — il contenuto di ogni volume persistent viene sostituito"
        },
        name: 'Nome del nuovo oggetto',
        storage: 'Storage',
        storageHint: 'Dove atterrano i dati ripristinati — il manifest non registra mai il pool perduto.',
        storageDeclared: 'Gli storage dichiarati nel manifest',
        bridge: "Bridge dell'host",
        bridgeHint:
          'Ogni scheda registrata torna su questo bridge con un MAC nuovo. Vuoto = nasce scollegato, il default più sicuro.',
        node: 'Nodo',
        nodeHint: "Il membro su cui l'App rinasce: il nodo è identità di cluster e non arriva mai dal manifest.",
        secretKeysWarning:
          'I valori env dichiarati segreti non viaggiano nel backup: la copia rinata li vuole reinseriti (il log del task nomina le chiavi).',
        overInventory: 'Stai per sovrascrivere questo oggetto',
        confirmType: 'Digita {name} per confermare',
        targetGone: "L'oggetto di origine non è più leggibile: resta il ripristino come nuovo.",
        targetLoading: "Lettura dell'oggetto bersaglio…",
        targetRunning:
          "L'oggetto bersaglio è acceso: il ripristino sostituisce i byte sotto il guest, quindi lo vuole spento."
      }
    }
  }
}
